/**
 * Marketing homepage content.
 *
 * Presentation lives in the components; every string, list and demo script
 * lives here so copy can be edited without touching layout or animation.
 */

export type NavItem = { label: string; href: string };

export const NAV_ITEMS: NavItem[] = [
  { label: 'Product', href: '#product' },
  { label: 'Solutions', href: '#solutions' },
  { label: 'Integrations', href: '#integrations' },
  { label: 'Pricing', href: '/pricing' },
  { label: 'Resources', href: '/blog' },
];

/**
 * The product app lives on its own subdomain, so these are absolute and leave
 * the marketing SPA entirely (same tab — it is the same product, not an
 * outbound link).
 */
export const APP_ORIGIN = 'https://app.vaanexa.com';
export const SIGN_IN_HREF = `${APP_ORIGIN}/login`;
export const START_FREE_HREF = `${APP_ORIGIN}/register`;

/** Stays on the marketing site — this is a CMS-authored page. */
export const DEMO_HREF = '/contact';

export const HERO = {
  eyebrow: 'WhatsApp & Instagram · AI conversations',
  headline: 'Turn every conversation into business.',
  headlineHighlight: 'into business',
  body: 'Vaanexa connects WhatsApp and Instagram with AI-powered conversations, lead management, follow-ups, bookings, quotations, payments and analytics—all from one intelligent platform.',
  primaryCta: 'Start Free',
  secondaryCta: 'Watch Vaanexa in Action',
} as const;

/* ------------------------------------------------------------------ *
 * Scroll-controlled product story
 * ------------------------------------------------------------------ */

export type JourneyStageId = 'connect' | 'knowledge' | 'ai' | 'qualify' | 'action' | 'analytics';

export type JourneyStage = {
  id: JourneyStageId;
  title: string;
  description: string;
  /** Short label shown in the stage rail. */
  label: string;
};

export const JOURNEY_STAGES: [JourneyStage, ...JourneyStage[]] = [
  {
    id: 'connect',
    label: 'Channels',
    title: 'Connect your channels',
    description: 'Bring WhatsApp and Instagram conversations into one workspace.',
  },
  {
    id: 'knowledge',
    label: 'Knowledge',
    title: 'Teach Vaanexa about your business',
    description: 'Add your services, products, policies and frequently asked questions.',
  },
  {
    id: 'ai',
    label: 'AI replies',
    title: 'Let AI handle the conversation',
    description: 'Give customers fast, relevant and consistent answers at any time.',
  },
  {
    id: 'qualify',
    label: 'Qualify',
    title: 'Qualify every opportunity',
    description: 'Capture customer details, understand intent and automatically score leads.',
  },
  {
    id: 'action',
    label: 'Convert',
    title: 'Move customers towards action',
    description:
      'Schedule appointments, generate quotations, collect payments and automate follow-ups.',
  },
  {
    id: 'analytics',
    label: 'Revenue',
    title: 'Understand what drives revenue',
    description: 'Track conversations, leads, conversions and team performance from one dashboard.',
  },
];

/* ------------------------------------------------------------------ *
 * Interactive AI conversation
 * ------------------------------------------------------------------ */

export const CONVERSATION = {
  customerName: 'Rohit Mehra',
  customerHandle: '+91 98••• ••210 · WhatsApp',
  customerMessage: 'Do you provide website development for real-estate companies?',
  aiReply:
    'Yes. We provide website development, lead-management systems and digital solutions for real-estate businesses. May I know how many projects you currently manage?',
} as const;

export type IntelField = {
  label: string;
  value: string;
  tone?: 'default' | 'positive' | 'accent';
  /** Renders as a 0–100 meter instead of a plain value. */
  meter?: number;
};

export const CONVERSATION_INTEL: IntelField[] = [
  { label: 'Requirement', value: 'Website and lead management' },
  { label: 'Industry', value: 'Real estate' },
  { label: 'Intent', value: 'High', tone: 'accent' },
  { label: 'Lead score', value: '87%', meter: 87, tone: 'accent' },
  { label: 'Status', value: 'Qualified', tone: 'positive' },
  { label: 'Follow-up', value: 'Scheduled', tone: 'positive' },
];

/* ------------------------------------------------------------------ *
 * Product capabilities (bento grid)
 * ------------------------------------------------------------------ */

export type CapabilityId =
  | 'ai-engine'
  | 'inbox'
  | 'pipeline'
  | 'knowledge'
  | 'followups'
  | 'appointments'
  | 'quotes'
  | 'analytics'
  | 'takeover'
  | 'team';

export type Capability = {
  id: CapabilityId;
  title: string;
  description: string;
  icon: string;
  /** Tailwind column/row span classes for the bento layout. */
  span: string;
};

export const CAPABILITIES: Capability[] = [
  {
    id: 'ai-engine',
    title: 'AI Conversation Engine',
    description:
      'Understands the requirement behind a message and answers from your own business knowledge.',
    icon: 'sparkles',
    span: 'lg:col-span-2',
  },
  {
    id: 'inbox',
    title: 'Unified Inbox',
    description: 'WhatsApp and Instagram threads in one queue, with assignment and status.',
    icon: 'inbox',
    span: 'lg:col-span-2',
  },
  {
    id: 'pipeline',
    title: 'Lead Pipeline',
    description: 'A visual board that moves each opportunity from new to won.',
    icon: 'kanban',
    span: 'lg:col-span-2',
  },
  {
    id: 'knowledge',
    title: 'Business Knowledge Base',
    description: 'Services, pricing rules, policies and FAQs the AI is allowed to use.',
    icon: 'book',
    span: 'lg:col-span-2',
  },
  {
    id: 'followups',
    title: 'Automated Follow-ups',
    description: 'Sequenced nudges that run until the customer replies or the deal closes.',
    icon: 'repeat',
    span: 'lg:col-span-2',
  },
  {
    id: 'appointments',
    title: 'Appointments',
    description: 'Share real availability and confirm bookings inside the chat.',
    icon: 'calendar',
    span: 'lg:col-span-2',
  },
  {
    id: 'quotes',
    title: 'Quotations and Payments',
    description: 'Generate a priced quotation and collect payment through a secure link.',
    icon: 'receipt',
    span: 'lg:col-span-3',
  },
  {
    id: 'analytics',
    title: 'Analytics',
    description: 'Conversations, leads, conversions and revenue in one view.',
    icon: 'chart',
    span: 'lg:col-span-3',
  },
  {
    id: 'takeover',
    title: 'Human Takeover',
    description: 'Any teammate can step into a live conversation and the AI steps back.',
    icon: 'handoff',
    span: 'lg:col-span-3',
  },
  {
    id: 'team',
    title: 'Team Management',
    description: 'Roles, permissions and ownership so the right person handles the right thread.',
    icon: 'users',
    span: 'lg:col-span-3',
  },
];

/* ------------------------------------------------------------------ *
 * Industry solutions
 * ------------------------------------------------------------------ */

export type Industry = {
  id: string;
  label: string;
  customerMessage: string;
  aiResponse: string;
  captured: string[];
  automation: string;
  outcome: string;
};

export const INDUSTRIES: [Industry, ...Industry[]] = [
  {
    id: 'real-estate',
    label: 'Real Estate',
    customerMessage: 'Is the 3BHK in Sector 62 still available? What is the budget range?',
    aiResponse:
      'Yes, that configuration is available. Prices depend on floor and facing. Would you like a site visit this week, or should I send the full floor plan first?',
    captured: [
      'Configuration: 3BHK',
      'Location: Sector 62',
      'Purpose: Purchase',
      'Timeline: This month',
    ],
    automation: 'Site visit booked and the floor plan sent automatically.',
    outcome: 'Enquiries reach a site visit without an agent chasing the thread.',
  },
  {
    id: 'education',
    label: 'Education',
    customerMessage:
      'What is the fee for the data-science programme and when does the batch start?',
    aiResponse:
      'The programme runs for 24 weeks with weekend and weekday batches. May I know your background so I can suggest the right track and share the fee structure?',
    captured: [
      'Programme: Data science',
      'Mode: Not decided',
      'Background: Asked',
      'Intake: Next batch',
    ],
    automation: 'Counselling call scheduled and the brochure delivered in chat.',
    outcome: 'Every enquiry gets a structured counselling conversation, not a missed reply.',
  },
  {
    id: 'healthcare',
    label: 'Healthcare',
    customerMessage: 'Do you have a dermatologist available on Saturday evening?',
    aiResponse:
      'Yes, evening slots are open on Saturday. May I know whether this is a first consultation or a follow-up so I can book the right duration?',
    captured: [
      'Speciality: Dermatology',
      'Visit type: Asked',
      'Preferred slot: Sat evening',
      'Patient: New',
    ],
    automation: 'Appointment confirmed with a reminder before the visit.',
    outcome: 'Fewer missed appointments and no queue at the reception desk.',
  },
  {
    id: 'professional-services',
    label: 'Professional Services',
    customerMessage: 'We need help with GST filing for a company with two branches.',
    aiResponse:
      'We handle multi-branch GST compliance. To prepare an accurate quotation, may I know your monthly invoice volume and whether both branches are in the same state?',
    captured: [
      'Service: GST compliance',
      'Entity: Private limited',
      'Branches: 2',
      'Volume: Asked',
    ],
    automation: 'Scoped quotation generated and sent for approval.',
    outcome: 'Scoping happens before your team spends billable time on the call.',
  },
  {
    id: 'ecommerce',
    label: 'E-commerce',
    customerMessage: 'My order was supposed to arrive yesterday. Can you check the status?',
    aiResponse:
      'I can check that for you. Could you confirm the order number or the phone number used at checkout?',
    captured: [
      'Intent: Order status',
      'Order: Awaiting ID',
      'Sentiment: Frustrated',
      'Priority: High',
    ],
    automation:
      'Status returned in chat, and the thread escalated to a human on negative sentiment.',
    outcome: 'Support load drops while genuine escalations reach a person quickly.',
  },
  {
    id: 'hospitality',
    label: 'Hospitality',
    customerMessage: 'Do you have a lake-view room for two nights this weekend?',
    aiResponse:
      'Lake-view rooms are available for those dates. Would you like breakfast included, and shall I hold the room while you confirm?',
    captured: ['Room: Lake view', 'Nights: 2', 'Guests: 2', 'Add-ons: Breakfast offered'],
    automation: 'Room held, payment link shared and the booking confirmed in chat.',
    outcome: 'Direct bookings get answered instantly instead of leaking to aggregators.',
  },
];

/* ------------------------------------------------------------------ *
 * Workflow
 * ------------------------------------------------------------------ */

export type WorkflowStep = { title: string; description: string; icon: string };

export const WORKFLOW_STEPS: WorkflowStep[] = [
  {
    title: 'Connect Channels',
    description: 'Link WhatsApp Business and Instagram in a guided setup.',
    icon: 'plug',
  },
  {
    title: 'Add Business Knowledge',
    description: 'Upload services, pricing rules, policies and FAQs.',
    icon: 'book',
  },
  {
    title: 'Configure AI',
    description: 'Set tone, boundaries, escalation rules and fallbacks.',
    icon: 'sliders',
  },
  {
    title: 'Receive Conversations',
    description: 'Customers message; Vaanexa answers and captures details.',
    icon: 'message',
  },
  {
    title: 'Convert Leads',
    description: 'Book, quote, collect payment and follow up until closed.',
    icon: 'target',
  },
];

/* ------------------------------------------------------------------ *
 * Trust and control
 * ------------------------------------------------------------------ */

export type TrustControl = { title: string; description: string; icon: string };

export const TRUST_HEADING = 'AI that follows your business—not the other way around.';

export const TRUST_CONTROLS: TrustControl[] = [
  {
    title: 'Business-controlled knowledge',
    description: 'The AI answers from the material you approve, and nothing else.',
    icon: 'book',
  },
  {
    title: 'Human takeover',
    description: 'A teammate can take any conversation; automation pauses on that thread.',
    icon: 'handoff',
  },
  {
    title: 'Conversation history',
    description: 'Full, searchable transcripts for every customer and channel.',
    icon: 'history',
  },
  {
    title: 'User roles and permissions',
    description: 'Scope who can view conversations, edit knowledge or publish changes.',
    icon: 'users',
  },
  {
    title: 'Approval rules',
    description: 'Require review before a quotation, discount or payment link goes out.',
    icon: 'check',
  },
  {
    title: 'Safe fallback responses',
    description: 'When confidence is low, the AI hands over instead of guessing.',
    icon: 'shield',
  },
  {
    title: 'Activity logs',
    description: 'An audit trail of who changed what, and when.',
    icon: 'list',
  },
  {
    title: 'Performance analytics',
    description: 'Measure response quality, resolution and conversion over time.',
    icon: 'chart',
  },
];

/* ------------------------------------------------------------------ *
 * Final CTA + footer
 * ------------------------------------------------------------------ */

export const FINAL_CTA = {
  heading: 'Your next customer is already messaging.',
  body: 'Let Vaanexa handle the conversation and help your team close the opportunity.',
  primary: 'Start Building with Vaanexa',
  secondary: 'Book a Product Demo',
} as const;

export const FOOTER_DESCRIPTION =
  'Vaanexa is an AI-powered customer conversation and sales automation platform for WhatsApp and Instagram. It answers customer questions, qualifies leads, books appointments, sends quotations, collects payments and reports on what actually drives revenue.';

export type FooterColumn = { heading: string; links: NavItem[] };

export const FOOTER_COLUMNS: FooterColumn[] = [
  {
    heading: 'Platform',
    links: [
      { label: 'Product', href: '#product' },
      { label: 'Solutions', href: '#solutions' },
      { label: 'Integrations', href: '#integrations' },
      { label: 'Pricing', href: '/pricing' },
    ],
  },
  {
    heading: 'Resources',
    links: [
      { label: 'Documentation', href: '/docs' },
      { label: 'Blog', href: '/blog' },
      { label: 'Contact', href: '/contact' },
    ],
  },
  {
    heading: 'Company',
    links: [
      { label: 'Privacy', href: '/privacy' },
      { label: 'Terms', href: '/terms' },
      { label: 'Sign In', href: SIGN_IN_HREF },
    ],
  },
];
